import differenceInSeconds from 'date-fns/differenceInSeconds';
import * as Sentry from '@sentry/react';
import { getAppInsightsInstance } from '../components/AppInsightsProvider/appInsights';
import ServerError from '../services/ServerError';
import NetworkError from '../services/NetworkError';

export const checkForNetworkError = (error) => {
    if (!(error instanceof TypeError)) return false;

    if (!error.message) return false;

    const networkErrorMsgs = [
        'Failed to fetch',
        'NetworkError when attempting to fetch resource',
        'Network request failed',
    ];
    return networkErrorMsgs.some((errMsg) => error.message.indexOf(errMsg) >= 0);
};

export function generateNewErrorCode(isTimeoutError) {
    const totalSeconds = differenceInSeconds(new Date(), new Date('1/1/1970'));
    return 'FE-' + totalSeconds.toString(36).toUpperCase() + (isTimeoutError ? '-TO' : ''); // base36 encode
}

let prevErrorHash = null;

/**
 * Strips query parameters and hash from a URL for clean error reporting
 * @param {string} url - The URL to clean
 * @returns {string} The cleaned URL with only origin and pathname
 */
function getCleanUrlForReporting(url) {
    if (!url) return url;
    
    try {
        const urlObj = new URL(url);
        return `${urlObj.origin}${urlObj.pathname}`;
    } catch {
        // If URL parsing fails, remove query string and hash manually
        return url.split('?')[0].split('#')[0];
    }
}

/**
 * Normalizes an error object for reporting by extracting the best available technical details
 * while avoiding generic user-facing messages
 * @param {Error|Object} error - The error object to normalize
 * @param {Object} responseJson - Optional server response JSON containing detailed error info
 * @returns {Error} A normalized error object with the best available technical details
 */
export function normalizeErrorForReporting(error, responseJson = null) {
    if (!error) return null;

    // For NetworkError objects, preserve them as-is since they already have proper formatting
    if (error instanceof NetworkError) {
        return error;
    }

    // Extract response JSON if it's embedded in the error
    const embeddedResponseJson = error?.responseJson || responseJson;

    // Create a new error object to avoid modifying the original
    const normalizedError = new Error();

    // Copy properties from the original error
    normalizedError.name = error.name || 'Error';
    normalizedError.stack = error.stack;

    // Copy additional properties if they exist
    if (error.status) normalizedError.status = error.status;
    if (error.statusText) normalizedError.statusText = error.statusText;
    if (error.url) normalizedError.url = error.url;
    if (embeddedResponseJson) normalizedError.responseJson = embeddedResponseJson;

    // Determine the best message to use
    let bestMessage = error.message;

    // If the error has the generic default message and we have server details, use them instead
    if (error.message === ServerError.DefaultErrorMessage && embeddedResponseJson?.message) {
        bestMessage = embeddedResponseJson.message;

        // Append validation errors if they exist
        if (embeddedResponseJson.validationErrors) {
            const validationErrorsConcat = Object.keys(embeddedResponseJson.validationErrors)
                ?.map((key) => `${key}: ${embeddedResponseJson.validationErrors[key]}`)
                ?.join(', ');
            if (validationErrorsConcat) {
                bestMessage += `: ${validationErrorsConcat}`;
            }
        }
    }

    normalizedError.message = bestMessage;
    return normalizedError;
}

/**
 * Builds a standardized object for error reporting, centralizing logging and message formatting.
 * This function prepares the error but does not send it, allowing the caller to control reporting.
 * @param {Error} err - The original error object.
 * @param {Object} [options={}] - Optional parameters.
 * @param {string} [options.errorMessage] - A custom base message for the error report.
 * @returns {Object} An object structured for use with the `reportError` function.
 */
export function buildErrorForReporting(err, options = {}) {
    const { errorMessage } = options;

    // Log non-server errors to the console. ServerErrors are logged by ApiService.
    if (err?.name !== 'ServerError') {
        console.error('An API call failed:', err);
    }

    // Build standardized error message
    let messageForReporting;
    
    // Handle errors with URLs (ServerError and NetworkError)
    
    if (err?.url) {
        const cleanUrl = getCleanUrlForReporting(err.url);
        messageForReporting = cleanUrl;

        switch (err.name) {
            case 'ServerError':
                if (err?.status) {
                    messageForReporting = `HTTP status code: ${err?.status}: ${cleanUrl} - ${err?.statusText || ''}`;
                } 
                break;
            case 'NetworkError':
                messageForReporting = `Network Connection Error: ${cleanUrl}`;
                break;
            default:
                // Fallback for other errors with URLs
                messageForReporting = `Error: ${cleanUrl}`;
                break;
        }
    } else {
        // Handle errors without URLs
        const errorCode = err?.responseJson?.errorCode;
        const baseMessage = errorMessage || err?.message || 'An unexpected error occurred';
        messageForReporting = errorCode ? `${baseMessage} (Error Code: ${errorCode})` : baseMessage;
    }

    return {
        errorObject: err,
        userFriendlyMessage: messageForReporting,
    };
}

/** * Reports an error to Sentry and Application Insights, handling deduplication and normalization.
 * This function centralizes error reporting logic, ensuring consistent handling across the application.
 * It checks the environment configuration to determine whether to report to Sentry and Application Insights.   
 * @param {Error|string|Object} error - The error to report. Can be an Error object, a string message, or an object with an `errorObject` property.
 * @returns {string|null} The error code if reporting was successful, or null if the error was a validation error or a duplicate.
 * @example reportError(new Error('Something went wrong'));
 */
export function reportError(error) {
    // If we receive a string, convert it to an error object for better reporting
    const errorToCapture = typeof error === 'string' ? new Error(error) : error;

    if (!errorToCapture) return;

    // Check environment configuration
    const sentryEnabled = !!window.environment?.SENTRY?.ENABLED;
    const appInsightsEnabled = !!window.environment?.APPINSIGHTS?.ENABLED;
    const serverErrorReportingEnabled =
        !!window.environment?.SENTRY?.SERVER_ERROR_REPORTING_ENABLED;

    // Extract error properties from the error structure
    // For direct Error objects (not wrapped in errorObject property), use the error directly
    const errorObject = errorToCapture?.errorObject || errorToCapture;
    const responseJson = errorObject.responseJson || errorToCapture?.responseJson || null;
    const apiUrl = errorObject?.url || errorToCapture?.url || null;

    // Determine error characteristics
    const serverErrorCode = responseJson?.errorCode;
    const isValidationError = errorObject.name === 'ValidationError';
    const isApiFailureError = errorObject.name === 'ApiFailureError';
    const isTimeoutError = !!errorObject.isTimeoutError;
    const isBackendError = !!serverErrorCode || isTimeoutError || isApiFailureError;

    // Skip validation errors (handled by form flow)
    if (isValidationError) return;

    // Generate error code
    const frontendErrorCode = !serverErrorCode ? generateNewErrorCode(isTimeoutError) : null;
    const errorCode = serverErrorCode || frontendErrorCode;

    // Create deduplication hash
    const validationErrorsStr = responseJson?.validationErrors
        ? Object.entries(responseJson.validationErrors)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ')
        : null;
    const dedupingHash = `${responseJson?.message || ''}-${validationErrorsStr || ''}-${
        apiUrl || ''
    }-${errorCode || ''}`;

    // Skip duplicate errors
    if (prevErrorHash === dedupingHash) return errorCode;
    prevErrorHash = dedupingHash;

    // Prepare error for reporting
    const originalError = errorObject;
    const normalizedError = normalizeErrorForReporting(originalError, responseJson);
    let errorToCaptureFinal = normalizedError || originalError;

    // Report to Application Insights
    if (appInsightsEnabled) {
        const appInsights = getAppInsightsInstance();
        appInsights.handleException(errorToCaptureFinal, { errorCode, isBackendError });
    }

    // Report to Sentry
    if (sentryEnabled && (serverErrorReportingEnabled || !isBackendError)) {
        reportToSentry(errorToCaptureFinal, {
            error,
            errorCode,
            apiUrl,
            responseJson,
            originalError,
            dedupingHash,
        });
    }

    return errorCode;
}

/**
 * Reports an error to Sentry with proper context and tags
 */
function reportToSentry(errorToCapture, context) {
    const { error, errorCode, apiUrl, responseJson, originalError, dedupingHash } = context;

    // Parse API URL for tags and fingerprinting
    let apiRoute = apiUrl;
    let queryString = '';

    if (apiUrl) {
        try {
            const parsedUrl = new URL(apiUrl);
            apiRoute = parsedUrl?.pathname;
            queryString = parsedUrl?.search;
        } catch {
            // Keep original apiUrl if parsing fails
        }
    }

    // Build tags
    const tags = { errorCode };
    if (apiUrl) tags.apiUrl = apiUrl;
    if (apiRoute) tags.apiRoute = apiRoute;
    if (queryString) tags.apiQueryString = queryString;

    // Add network-specific tags for NetworkError objects
    if (errorToCapture instanceof NetworkError) {
        tags.networkFailureType = errorToCapture.networkFailureType;
        tags.domain = errorToCapture.domain;
        tags.isNetworkError = true;
    }

    // Include any custom tags from the error
    if (error?.tags) {
        Object.assign(tags, error.tags);
    }

    Sentry.withScope((scope) => {
        // Set tags for filtering
        Object.entries(tags).forEach(([key, value]) => {
            scope.setTag(key, value);
        });

        // Set error context
        scope.setContext('errorDetails', {
            displayMessage: error?.userFriendlyMessage,
            originalErrorName: originalError?.name,
            hasResponseJson: !!responseJson,
            apiQueryString: queryString,
            dedupingHash,
        });

        scope.setContext('httpResponse', originalError);

        // Set server response context if available
        if (responseJson) {
            scope.setContext('serverResponse', {
                errorCode: responseJson.errorCode,
                message: responseJson.message,
                validationErrors: responseJson.validationErrors,
                wasSuccessful: responseJson.wasSuccessful,
            });
        }

        // Set fingerprint for better error grouping
        const fingerprint = ['{{ default }}'];
        if (apiRoute) {
            fingerprint.push(apiRoute);
        }
        scope.setFingerprint(fingerprint);

        // Capture the exception
        Sentry.captureException(errorToCapture);
    });
}

export const getValidationErrorStringFromResponse = async (response, fallbackMessage) => {
    const { validationErrors } = response || {};

    const values = Object.values(validationErrors);

    if (!values.length) return fallbackMessage;

    const message = values.reduce((acc, err, index) => {
        acc = `${acc}${index !== values?.length - 1 ? ' ,' : ''}${err}`;
        return acc;
    }, '');

    return message;
};

/**
 * Converts technical error messages into user-friendly messages
 * @param {Error} error - The original error object
 * @returns {string} A user-friendly error message
 */
export const getUserFriendlyErrorMessage = (error) => {
    // Check for network-related errors
    if (checkForNetworkError(error)) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
    } else if (
        error.name === 'TimeoutError' ||
        (error.message && error.message.toLowerCase().includes('timeout'))
    ) {
        return 'The request timed out. Please try again later.';
    } else if (error instanceof NetworkError) {
        return 'Unable to connect to the server. Please check your internet connection and try again.';
    } else if (error instanceof ServerError) {
        if (error.status === 500) {
            return 'An unexpected server error occurred. Our team has been notified.';
        } else if (error.status === 404) {
            return 'The requested resource was not found.';
        } else if (error.status === 403) {
            return 'You do not have permission to access this resource.';
        } else if (error.status === 401) {
            return 'Your session may have expired. Please try logging in again.';
        } else if (error.status >= 400 && error.status < 500) {
            return (
                error.message ||
                'There was an issue with the request. Please verify your information.'
            );
        }
    }
    // Return the original message if no specific handling is defined
    return error.message || 'An unexpected error occurred.';
};

/**
 * Initializes Sentry with modern 2025 best practices
 * Should be called once at application startup, before React renders
 */
export const initializeSentry = () => {
    console.debug('🚀 Sentry initialization starting...');

    // Prevent multiple initializations
    if (window.__SENTRY_INITIALIZED) {
        console.debug('✅ Sentry already initialized, skipping initialization.');
        return;
    }

    // Get environment configuration from window object (set by env.js)
    const environment = window.environment || {};
    const { SENTRY, ENVIRONMENT, PORTAL_NAME } = environment;
    const frontendVersion = process.env.REACT_APP_VERSION;

    console.debug('🔧 Sentry config check:', {
        SENTRY_ENABLED: SENTRY?.ENABLED,
        DEBUG_MODE: SENTRY?.DEBUG,
        ENVIRONMENT,
        PORTAL_NAME,
        DSN_EXISTS: !!SENTRY?.DSN,
    });

    // Skip initialization if Sentry is disabled
    if (!SENTRY?.ENABLED) {
        console.debug('⚠️ Sentry is disabled, skipping initialization.');
        return;
    }

    try {
        Sentry.init({
            dsn: SENTRY?.DSN,
            environment: ENVIRONMENT,
            debug: !!SENTRY?.DEBUG, // Set to true for development to see debug logs
            release: frontendVersion || 'No frontend version found',
            integrations: [
                // Browser tracing for performance monitoring
                Sentry.browserTracingIntegration(),

                // Session replay if enabled
                ...(SENTRY?.REPLAY_ENABLED
                    ? [
                          Sentry.replayIntegration({
                              maskAllText: false,
                              blockAllMedia: false,
                              mask: ['.phi'], // Mask elements with class "phi" for PHI/PII data
                          }),
                      ]
                    : []),
            ],

            // Performance monitoring configuration
            tracesSampleRate: ENVIRONMENT === 'Local' || ENVIRONMENT === 'Dev' ? 1.0 : 0.1,
            tracePropagationTargets: [
                /^\//, // Same origin
                /^https:\/\/.*\.carecontinuity\.com/,
            ],

            // Session replay configuration
            replaysSessionSampleRate: PORTAL_NAME === 'patient' ? 0.1 : 0.05,
            replaysOnErrorSampleRate: 1.0,

            // Error filtering logic
            beforeSend: (event, hint) => {
                const message = hint?.originalException || { name: event?.message };

                if (isIgnoredError(message, !!SENTRY?.SERVER_ERROR_REPORTING_ENABLED)) {
                    return null;
                }

                // Normalize error message before sending to Sentry
                if (hint?.originalException) {
                    try {
                        const normalizedError = normalizeErrorForReporting(hint.originalException);
                        if (
                            normalizedError &&
                            normalizedError.message !== hint.originalException.message
                        ) {
                            if (event?.exception?.values?.[0]) {
                                event.exception.values[0].value = normalizedError.message;
                            }
                            if (event?.message) {
                                event.message = normalizedError.message;
                            }
                        }
                    } catch (normalizationError) {
                        console.warn('Error normalizing exception for Sentry:', normalizationError);
                    }
                }

                return event;
            },

            autoSessionTracking: true,
        });

        // Set initialized flag on window object
        window.__SENTRY_INITIALIZED = true;
        console.debug('✅ Sentry initialized successfully.');

        // Report any early errors that were captured before Sentry initialized
        console.debug('🔍 Checking for early errors...');
        console.debug('🔍 window.earlyErrors exists:', !!window.earlyErrors);
        console.debug('🔍 window.earlyErrors length:', window.earlyErrors?.length);

        if (window.earlyErrors && window.earlyErrors.length > 0) {
            console.debug(`📋 Reporting ${window.earlyErrors.length} early error(s) to Sentry...`);
            window.earlyErrors.forEach((earlyError, index) => {
                const error = earlyError.error || new Error(earlyError.message);
                error.stack =
                    error.stack ||
                    `at ${earlyError.filename}:${earlyError.lineno}:${earlyError.colno}`;

                Sentry.withScope((scope) => {
                    scope.setTag('earlyError', 'true');
                    scope.setContext('earlyErrorDetails', {
                        capturedAt: new Date(earlyError.timestamp).toISOString(),
                        filename: earlyError.filename,
                        lineno: earlyError.lineno,
                        colno: earlyError.colno,
                        errorIndex: index,
                    });
                    console.debug(`📋 Sending early error ${index + 1} to Sentry:`, error.message);
                    Sentry.captureException(error);
                });
            });

            console.debug('📋 Early errors reported to Sentry.');
        } else {
            console.debug('📋 No early errors to report.');
        }

        // Always clear the early errors array and remove event listeners after Sentry initialization
        // This prevents memory leaks regardless of whether early errors existed
        window.earlyErrors = [];

        // Clear persisted early errors from sessionStorage
        try {
            sessionStorage.removeItem('earlyErrors');
            console.debug('📋 Cleared early errors from sessionStorage.');
        } catch (e) {
            console.debug('📋 Failed to clear early errors from sessionStorage:', e);
        }

        // Remove the early error event listener since Sentry is now handling errors
        if (window.__earlyErrorHandler) {
            window.removeEventListener('error', window.__earlyErrorHandler);
            window.removeEventListener('unhandledrejection', window.__earlyErrorHandler);
            delete window.__earlyErrorHandler;
            console.debug('📋 Early error event listeners removed.');
        }
    } catch (error) {
        console.error('Error initializing Sentry:', error);
    }
};

// List of error types that should be ignored by Sentry
const IgnoredErrorTypes = [
    'UnauthorizedError',
    'ResizeObserver',
    'Object Not Found Matching Id',
    'AbortError',
];

const isIgnoredError = (e, shouldReportServerErrors) => {
    if (!e) return false;

    if (IgnoredErrorTypes.some((errorType) => e.name?.includes(errorType))) return true;

    if (e.name === 'ServerError')
        return e.isTimeoutError || shouldReportServerErrors ? false : true;

    return false;
};